import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Building, Users, FileText, Settings, LogOut, Menu, Search, Bell,
  User, Mail, Palette, Shield, Send, FolderTree, Building2, GitBranch,
  Users2, FolderOpen, MessageSquare, ListChecks, BarChart4, BarChart3,
  Download, ChevronDown, ChevronRight, BookTemplate, Link2, Sun, Moon,
  Monitor, BookOpen, SlidersHorizontal
} from 'lucide-react';
import logo from '../../assets/logo/logo-gwpc-light.png';

/* ------------------------------------------------------------------
   Odoo-style application chrome.

   All colour comes from the tokens in theme.css, so this layout — and
   every screen inside it — follows light/dark with no per-component
   work. Do not hardcode hex values here.

   Structure:
     - one dark app bar at the top (logo, app links, theme toggle,
       search, notifications, user menu)
     - one flat sidebar at the left (company switcher, expandable nav
       groups, user block pinned at the bottom)
     - a control-panel strip under the app bar owning the breadcrumb;
       screens inject their own actions via the `controlPanel` prop
------------------------------------------------------------------- */

/* `bare` — for screens that render their OWN control-panel strip (the
   ported EmployeeList, ReportsDashboard and EmployeeSurveyReport all
   do). Without it you get the page title twice: once from Layout's
   breadcrumb, once from the screen. */
const Layout = ({ children, controlPanel, bare = false }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const { theme, preference, isDark, setTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);   // mobile drawer
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Building, type: 'single' },
    {
      name: 'Company Structure', icon: Building2, type: 'dropdown',
      items: [
        { name: 'Companies', href: '/company', icon: Building },
        { name: 'Branches', href: '/branches', icon: GitBranch },
        { name: 'Divisions', href: '/divisions', icon: FolderTree },
        { name: 'Departments', href: '/departments', icon: FolderOpen },
        { name: 'Employees', href: '/employees', icon: Users2 }
      ]
    },
    {
      name: 'Surveys', icon: FileText, type: 'dropdown',
      items: [
        { name: 'Survey Forms', href: '/surveys', icon: FileText },
        { name: 'Survey Categories', href: '/survey-categories-manager', icon: FolderTree },
        { name: 'Survey Questions', href: '/survey-questions', icon: ListChecks },
        { name: 'Survey Link', href: '/survey-links', icon: Link2 },
        { name: 'Send Survey', href: '/send-survey', icon: Send },
        { name: 'Send Reminders', href: '/send-reminders', icon: Bell }
      ]
    },
    {
      name: 'Survey Responses', icon: MessageSquare, type: 'dropdown',
      items: [
        { name: 'All Responses', href: '/survey-responses', icon: MessageSquare },
        { name: 'Question Responses', href: '/question-responses', icon: ListChecks }
      ]
    },
    {
      /* The 7 report types below each have their own distinct on-screen
         report AND their own distinct PDF export -- both already built in
         ReportsCenter.jsx / ReportPdfController.cs, which branch on
         "?tab=<id>" (read via useSearchParams on load). They used to be
         mis-wired to /reports/by-reports (the generic filter-driven "All
         Reports" combined dashboard, which ignores ?tab= entirely) which is
         why every one of these looked identical on screen and produced the
         exact same "Organizational Breakdown" PDF no matter which was
         clicked. Point them at /reports/center instead so each one lands on
         its own real tab. The combined multi-branch/department/division
         dashboard (with its own Excel export) is kept reachable as its own
         "All Reports (Combined)" entry rather than removed. */
      name: 'Reports', icon: BarChart4, type: 'dropdown',
      items: [
        { name: 'Employee Reports', href: '/reports/employee-surveys', icon: BarChart3 },
        { name: 'Response & Participation', href: '/reports/center?tab=participation', icon: Users2 },
        { name: 'Rating Distribution', href: '/reports/center?tab=distribution', icon: BarChart3 },
        { name: 'Category Performance', href: '/reports/center?tab=category', icon: FolderTree },
        { name: 'Question Performance', href: '/reports/center?tab=question', icon: ListChecks },
        { name: 'Organizational Comparison', href: '/reports/center?tab=organization', icon: GitBranch },
        { name: 'Trend / Period Comparison', href: '/reports/center?tab=trend', icon: BarChart4 },
        { name: 'Management Summary', href: '/reports/center?tab=summary', icon: FileText },
        { name: 'All Reports (Combined)', href: '/reports/by-reports', icon: BarChart4 },
        { name: 'Average Reports', href: '/reports/employee-average', icon: BarChart4 }
      ]
    },
    {
      name: 'Email', icon: Mail, type: 'dropdown',
      items: [
        { name: 'Email Setup', href: '/email-setup', icon: Settings },
        { name: 'Email Templates', href: '/email-templates', icon: BookTemplate }
      ]
    },
    { name: 'Transfer Survey', href: '/transfer-survey', icon: Download, type: 'single' },
    { name: 'Users & Permissions', href: '/users-permissions', icon: Shield, type: 'single' },
    { name: 'Profile', href: '/profile', icon: User, type: 'single' },
    { name: 'Themes', href: '/themes', icon: Palette, type: 'single' },
    { name: 'Settings', href: '/settings', icon: Settings, type: 'single' }
  ];

  const filteredNavigation = navigation.filter((item) => {
    // Users & Permissions and Transfer Survey both call SuperAdmin-only
    // backend endpoints (TransferSurveyController is [Authorize(Roles =
    // "SuperAdmin")] -- it moves survey data across tenants, so it isn't
    // scoped to a single company like most other endpoints). Showing these
    // links to a CompanyAdmin/Employee just sends them to a page that 403s
    // on load ("Failed to load companies"), so hide both for non-SuperAdmins
    // instead of only the first one. Settings is app-wide configuration
    // (branding, system info, etc.) rather than per-company, so it's also
    // SuperAdmin-only in the sidebar.
    if (item.name === 'Users & Permissions' || item.name === 'Transfer Survey' || item.name === 'Settings') {
      return isSuperAdmin;
    }
    return true;
  });

  // Several report links below share one pathname ("/reports/by-reports")
  // and are only distinguished by a "?tab=" query string, so a plain
  // pathname comparison would make all of them light up together. When an
  // href carries a query string, match the full pathname+search instead.
  const isActive = (href) => {
    if (href.includes('?')) return `${location.pathname}${location.search}` === href;
    return location.pathname === href;
  };
  const hasActiveChild = (items) => items?.some((c) => isActive(c.href));

  useEffect(() => {
    const next = {};
    navigation.forEach((item) => {
      if (item.type === 'dropdown' && hasActiveChild(item.items)) next[item.name] = true;
    });
    setOpenMenus((prev) => ({ ...prev, ...next }));
    setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const toggleMenu = (name) =>
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));

  const getCompanyName = () =>
    user?.companyName ||
    user?.company?.name ||
    (user?.companyId ? `Company #${user.companyId}` : 'No Company');

  const roleLabel =
    user?.userType === 0 ? 'Super Admin' : user?.userType === 1 ? 'Company Admin' : 'Employee';

  const roleTone = user?.userType === 0 ? 'teal' : user?.userType === 1 ? 'blue' : 'grey';

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';

  const currentPageTitle = (() => {
    for (const item of filteredNavigation) {
      if (item.type === 'single' && isActive(item.href)) return item.name;
      if (item.type === 'dropdown') {
        const child = item.items?.find((c) => isActive(c.href));
        if (child) return child.name;
      }
    }
    return 'Dashboard';
  })();

  const currentSection = (() => {
    for (const item of filteredNavigation) {
      if (item.type === 'dropdown' && hasActiveChild(item.items)) return item.name;
    }
    return null;
  })();

  const topIconBtn = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    background: 'transparent',
    border: 0,
    borderRadius: 3,
    color: 'var(--topbar-ink)',
    cursor: 'pointer'
  };

  const menuRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '7px 13px',
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    fontSize: 12.5,
    textAlign: 'left',
    color: 'var(--body)'
  };

  const hoverIn = (e) => (e.currentTarget.style.background = 'var(--hover)');
  const hoverOut = (e) => (e.currentTarget.style.background = 'transparent');

  const topHoverIn = (e) => {
    e.currentTarget.style.background = 'var(--topbar-hover)';
    e.currentTarget.style.color = 'var(--topbar-ink-strong)';
  };
  const topHoverOut = (e) => {
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.color = 'var(--topbar-ink)';
  };

  /* ---------------- sidebar item renderers ---------------- */

  const singleItem = (item) => {
    const active = isActive(item.href);
    const Icon = item.icon;
    return (
      <Link
        key={item.name}
        to={item.href}
        title={sidebarCollapsed ? item.name : ''}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-[9px] rounded-[3px] px-2 py-[6px] text-[12.5px] transition-colors ${
          sidebarCollapsed ? 'justify-center' : ''
        }`}
        style={{
          background: active ? 'var(--tint)' : 'transparent',
          color: active ? 'var(--tint-ink)' : 'var(--body-2)',
          fontWeight: active ? 600 : 500
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hover)'; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon className="h-[15px] w-[15px] flex-shrink-0" strokeWidth={1.8} />
        {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
      </Link>
    );
  };

  const dropdownItem = (item) => {
    const Icon = item.icon;
    const open = !!openMenus[item.name];
    const childActive = hasActiveChild(item.items);

    if (sidebarCollapsed) {
      return (
        <div key={item.name} className="relative">
          <button
            type="button"
            title={item.name}
            onClick={() => toggleMenu(item.name)}
            className="flex w-full items-center justify-center rounded-[3px] px-2 py-[6px] transition-colors"
            style={{
              background: childActive ? 'var(--tint)' : 'transparent',
              color: childActive ? 'var(--tint-ink)' : 'var(--body-2)'
            }}
          >
            <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
          </button>
          {open && (
            <div
              className="absolute left-full top-0 z-50 ml-1 w-52 rounded-[4px] py-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
            >
              {item.items?.map((child) => {
                const active = isActive(child.href);
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={() => { setSidebarOpen(false); toggleMenu(item.name); }}
                    className="flex items-center gap-[9px] px-3 py-[6px] text-[12.5px] transition-colors"
                    style={{
                      background: active ? 'var(--tint)' : 'transparent',
                      color: active ? 'var(--tint-ink)' : 'var(--muted)',
                      fontWeight: active ? 600 : 400
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hover)'; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <child.icon className="h-[14px] w-[14px]" strokeWidth={1.8} />
                    {child.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={item.name}>
        <button
          type="button"
          onClick={() => toggleMenu(item.name)}
          className="flex w-full items-center gap-[9px] rounded-[3px] px-2 py-[6px] text-left text-[12.5px] transition-colors"
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: childActive ? 'var(--tint-ink)' : 'var(--body-2)',
            fontWeight: childActive ? 600 : 500
          }}
          onMouseEnter={hoverIn}
          onMouseLeave={hoverOut}
        >
          <Icon className="h-[15px] w-[15px] flex-shrink-0" strokeWidth={1.8} />
          <span className="flex-1 truncate">{item.name}</span>
          {open
            ? <ChevronDown className="h-[11px] w-[11px] opacity-60" strokeWidth={2.6} />
            : <ChevronRight className="h-[11px] w-[11px] opacity-60" strokeWidth={2.6} />}
        </button>

        {open && (
          <div
            className="mb-1 ml-[19px] mt-px flex flex-col gap-px pl-[9px]"
            style={{ borderLeft: '1px solid var(--line-2)' }}
          >
            {item.items?.map((child) => {
              const active = isActive(child.href);
              return (
                <Link
                  key={child.name}
                  to={child.href}
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-[3px] px-2 py-[5px] text-[12.5px] transition-colors"
                  style={{
                    background: active ? 'var(--tint)' : 'transparent',
                    color: active ? 'var(--tint-ink)' : 'var(--muted)',
                    fontWeight: active ? 600 : 400
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hover)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {child.name}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const sidebarBody = (
    <>
      {!sidebarCollapsed && (
        <div className="px-3 pb-[9px] pt-[11px]" style={{ borderBottom: '1px solid var(--line-2)' }}>
          <div
            className="mb-[5px] text-[10px] font-semibold uppercase tracking-[.09em]"
            style={{ color: 'var(--faint)' }}
          >
            Current company
          </div>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-[4px] px-2 py-[6px] text-left transition-colors"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--line)')}
          >
            <span className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
              {getCompanyName()}
            </span>
            <ChevronDown className="h-3 w-3 flex-shrink-0" style={{ color: 'var(--faint)' }} strokeWidth={2.4} />
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 pb-4 pt-[6px]">
        <div className="flex flex-col gap-px">
          {filteredNavigation.map((item) =>
            item.type === 'single' ? singleItem(item) : dropdownItem(item)
          )}
        </div>
      </nav>

      <div
        className="flex flex-shrink-0 items-center gap-[9px] px-[10px] py-2"
        style={{ borderTop: '1px solid var(--line-2)' }}
      >
        <Link
          to="/profile"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
        >
          {initials}
        </Link>
        {!sidebarCollapsed && (
          <>
            <div className="min-w-0 flex-1">
              <Link to="/profile" className="block truncate text-[12px] font-semibold" style={{ color: 'var(--ink)' }}>
                {user?.firstName} {user?.lastName}
              </Link>
              <div className="text-[10.5px]" style={{ color: 'var(--faint)' }}>{roleLabel}</div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Log out"
              className="flex h-[26px] w-[26px] items-center justify-center rounded-[3px] transition-colors"
              style={{ color: 'var(--faint)', background: 'transparent', border: 0, cursor: 'pointer' }}
              onMouseEnter={hoverIn}
              onMouseLeave={hoverOut}
            >
              <LogOut className="h-[14px] w-[14px]" strokeWidth={1.9} />
            </button>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden" style={{ background: 'var(--bg)', color: 'var(--body)' }}>
      {/* ══ TOP APP BAR ══ */}
      <header
        className="flex h-[46px] flex-shrink-0 items-center gap-1 pl-1 pr-[10px]"
        style={{ background: 'var(--topbar)', color: 'var(--topbar-ink)' }}
      >
        <button
          type="button"
          onClick={() => {
            if (window.innerWidth < 1024) setSidebarOpen((v) => !v);
            else setSidebarCollapsed((v) => !v);
          }}
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[3px]"
          style={{ background: 'transparent', border: 0, color: 'var(--topbar-ink)', cursor: 'pointer' }}
          onMouseEnter={topHoverIn}
          onMouseLeave={topHoverOut}
          aria-label="Toggle navigation"
        >
          <Menu className="h-[17px] w-[17px]" strokeWidth={2} />
        </button>

        <Link
          to="/dashboard"
          className="mr-[6px] flex items-center gap-2 pr-[14px]"
          style={{ borderRight: '1px solid var(--topbar-rule)' }}
        >
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            className="h-[22px] w-auto max-w-[120px] object-contain"
            style={{ filter: 'var(--logo-filter)' }}
          />
          <span
            className="hidden whitespace-nowrap text-[14.5px] font-semibold sm:block"
            style={{ color: 'var(--topbar-ink-strong)' }}
          >
            Great Work Place Club
          </span>
        </Link>

        <nav className="flex min-w-0 flex-1 items-center gap-[2px] overflow-hidden">
          {[
            ['Surveys', '/surveys'],
            ['Employees', '/employees'],
            ['Reporting', '/reports/by-reports'],
            // Settings is SuperAdmin-only (see filteredNavigation above), so
            // don't offer a second way to it from the top bar for other roles.
            ...(isSuperAdmin ? [['Configuration', '/settings']] : [])
          ].map(([label, href]) => {
            const active = location.pathname === href;
            return (
              <Link
                key={label}
                to={href}
                className="hidden h-[30px] items-center whitespace-nowrap rounded-[3px] px-[11px] text-[12.5px] font-medium md:flex"
                style={{ color: active ? 'var(--topbar-ink-strong)' : 'var(--topbar-ink)' }}
                onMouseEnter={topHoverIn}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = active ? 'var(--topbar-ink-strong)' : 'var(--topbar-ink)';
                }}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-[2px]">
          {/* light / dark */}
          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={topIconBtn}
            onMouseEnter={topHoverIn}
            onMouseLeave={topHoverOut}
          >
            {isDark ? <Sun className="h-4 w-4" strokeWidth={1.9} /> : <Moon className="h-4 w-4" strokeWidth={1.9} />}
          </button>

          <button
            type="button"
            style={topIconBtn}
            onMouseEnter={topHoverIn}
            onMouseLeave={topHoverOut}
            aria-label="Search"
          >
            <Search className="h-4 w-4" strokeWidth={2} />
          </button>

          <button
            type="button"
            className="relative"
            style={topIconBtn}
            onMouseEnter={topHoverIn}
            onMouseLeave={topHoverOut}
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4" strokeWidth={2} />
          </button>

          {/* ══ USER MENU ══ */}
          <div className="relative ml-1" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-[7px] rounded-full py-[3px] pl-[3px] pr-[8px]"
              style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--topbar-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[11px] font-semibold"
                style={{ background: 'var(--avatar-bg)', color: 'var(--avatar-ink)' }}
              >
                {initials}
              </span>
              <span
                className="hidden whitespace-nowrap text-[12.5px] sm:block"
                style={{ color: 'var(--topbar-ink-strong)' }}
              >
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDown
                className="h-[11px] w-[11px] flex-shrink-0 opacity-70 transition-transform"
                strokeWidth={2.6}
                style={{ transform: userMenuOpen ? 'rotate(180deg)' : 'none' }}
              />
            </button>

            {userMenuOpen && (
              <div
                className="absolute right-0 top-[40px] z-[80] w-[250px] overflow-hidden rounded-[5px]"
                style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
              >
                {/* identity */}
                <div
                  className="flex items-center gap-2.5 px-[13px] py-3"
                  style={{ borderBottom: '1px solid var(--line-soft)' }}
                >
                  <span
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-semibold"
                    style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
                  >
                    {initials}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="block truncate text-[11.5px]" style={{ color: 'var(--faint)' }}>
                      {user?.email}
                    </span>
                  </span>
                </div>

                {/* role + company */}
                <div
                  className="flex items-center gap-2 px-[13px] py-2"
                  style={{ borderBottom: '1px solid var(--line-soft)' }}
                >
                  <span
                    className="inline-block whitespace-nowrap rounded-[10px] px-2 py-px text-[11px] font-semibold"
                    style={{ background: `var(--b-${roleTone}-bg)`, color: `var(--b-${roleTone}-fg)` }}
                  >
                    {roleLabel}
                  </span>
                  <span className="truncate text-[11.5px]" style={{ color: 'var(--muted)' }}>
                    {getCompanyName()}
                  </span>
                </div>

                {/* actions */}
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
                    style={menuRow}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    <User className="h-[14px] w-[14px] flex-shrink-0 opacity-75" strokeWidth={1.8} />
                    <span className="flex-1">My profile</span>
                  </button>
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                      style={menuRow}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut}
                    >
                      <SlidersHorizontal className="h-[14px] w-[14px] flex-shrink-0 opacity-75" strokeWidth={1.8} />
                      <span className="flex-1">Preferences</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setUserMenuOpen(false); navigate('/themes'); }}
                    style={menuRow}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    <Palette className="h-[14px] w-[14px] flex-shrink-0 opacity-75" strokeWidth={1.8} />
                    <span className="flex-1">Themes</span>
                  </button>
                </div>

                {/* appearance — light / dark / system, matching ThemeContext */}
                <div className="px-[13px] py-2" style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <div
                    className="mb-1.5 text-[10px] font-semibold uppercase tracking-[.09em]"
                    style={{ color: 'var(--faint)' }}
                  >
                    Appearance
                  </div>
                  <div className="flex overflow-hidden rounded-[3px]" style={{ border: '1px solid var(--line-3)' }}>
                    {[
                      { key: 'light', label: 'Light', Icon: Sun },
                      { key: 'dark', label: 'Dark', Icon: Moon },
                      { key: 'system', label: 'Auto', Icon: Monitor }
                    ].map(({ key, label, Icon }, i, arr) => {
                      const active = preference === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setTheme(key)}
                          className="flex flex-1 items-center justify-center gap-1 py-[5px] text-[11.5px]"
                          style={{
                            background: active ? 'var(--tint)' : 'var(--surface)',
                            color: active ? 'var(--tint-ink)' : 'var(--muted)',
                            fontWeight: active ? 600 : 500,
                            border: 0,
                            borderRight: i === arr.length - 1 ? 0 : '1px solid var(--line-3)',
                            cursor: 'pointer'
                          }}
                        >
                          <Icon className="h-[12px] w-[12px]" strokeWidth={1.9} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="py-1" style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <button
                    type="button"
                    onClick={handleLogout}
                    style={{ ...menuRow, color: 'var(--neg)' }}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    <LogOut className="h-[14px] w-[14px] flex-shrink-0 opacity-75" strokeWidth={1.8} />
                    <span className="flex-1">Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 top-[46px] z-30 bg-black/30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ══ SIDEBAR ══ */}
        <aside
          className={`fixed inset-y-0 left-0 top-[46px] z-40 flex flex-col transition-transform duration-200 lg:static lg:top-0 lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            width: sidebarCollapsed ? 56 : 236,
            flex: `0 0 ${sidebarCollapsed ? 56 : 236}px`,
            background: 'var(--sidebar)',
            borderRight: '1px solid var(--line)'
          }}
        >
          {sidebarBody}
        </aside>

        {/* ══ MAIN ══ */}
        <main className="flex min-w-0 flex-1 flex-col" style={{ background: 'var(--surface)' }}>
          {/* control panel */}
          {!bare && (
          <div className="flex-shrink-0" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
              <div className="flex min-w-0 items-baseline gap-[7px]">
                {currentSection && (
                  <>
                    <span className="hidden text-[13px] sm:block" style={{ color: 'var(--accent)' }}>
                      {currentSection}
                    </span>
                    <span className="hidden sm:block" style={{ color: 'var(--dim)' }}>/</span>
                  </>
                )}
                <h1
                  className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
                  style={{ color: 'var(--ink)' }}
                >
                  {currentPageTitle}
                </h1>
              </div>

              {/* Screens inject their own New / Export / search / pager here. */}
              {controlPanel && <div className="flex flex-1 flex-wrap items-center gap-2">{controlPanel}</div>}
            </div>
          </div>
          )}

          {/* page content */}
          <div className="flex-1 overflow-y-auto" style={{ background: 'var(--bg)' }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
